import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusCodes } from 'http-status-codes';

import { AddPaymentMethodModal } from 'components/Payment/Modals/AddPaymentMethodModal/AddPaymentMethodModal';
import { RetryPaymentSuccessModal } from 'components/Payment/Modals/RetryPaymentSuccessModal/RetryPaymentSuccessModal';
import { UpdatePaymentConfirmModal } from 'components/Payment/Modals/UpdatePaymentConfirmModal/UpdatePaymentConfirmModal';

import ClientsService from 'features/client/clientService';
import { getClientPaymentMethodsInfo } from 'features/client/clientThunks';
import { selectActiveJobDetails } from 'features/jobs/jobsSelectors';
import jobsService from 'features/jobs/jobsService';
import { fetchActiveJobDetails, fetchJobsData } from 'features/jobs/jobsThunks';

import useErrorCatch from 'hooks/useErrorCatch';
import usePageviewEvent from 'hooks/usePageViewEvent';
import { useAppDispatch, useAppSelector } from 'hooks/useTypedRedux';

import { notify } from 'utils/toast/createToast';

import { AlertType, GTMEvents, JobStatus } from 'constants/enums';
import { SUBSCRIPTION_STATUSES_REQUIRING_UPDATE_PAYMENT_CONFIRM } from 'constants/jobsSettings';
import { DEBOUNCE_JOBS_TIMEOUT_MS } from 'constants/uiSettings';

import JobInfoView from './JobInfoView/JobInfoView';

interface JobInfoControllerProps {
  jobId: string | null;
  onLoadError?: () => void;
}

const JobInfoController = ({ jobId, onLoadError }: JobInfoControllerProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const catchError = useErrorCatch();
  const activeJobDetails = useAppSelector(selectActiveJobDetails);

  const [loadingJobId, setLoadingJobId] = useState<string | null>(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [isPaymentMethodModalVisible, setIsPaymentMethodModalVisible] =
    useState(false);
  const [
    isUpdatePaymentConfirmModalVisible,
    setIsUpdatePaymentConfirmModalVisible
  ] = useState(false);
  const [
    isRetryPaymentSuccessModalVisible,
    setIsRetryPaymentSuccessModalVisible
  ] = useState(false);
  const [isRetryPayment, setIsRetryPayment] = useState(false);
  const currentJobIdRef = useRef<string | null>(null);
  const refreshTimersRef = useRef<{ jobs?: number; details?: number }>({});

  usePageviewEvent(GTMEvents.JobListViewSidebar);

  useEffect(() => {
    currentJobIdRef.current = jobId;
  }, [jobId]);

  const loadActiveJobDetails = useCallback(
    async (jobId: string): Promise<void> => {
      setLoadingJobId(jobId);
      try {
        await dispatch(fetchActiveJobDetails(jobId)).unwrap();
      } catch (error) {
        catchError({
          error,
          default: {
            message: t('error.message.operationFailed'),
            description: t('error.message.failedFetchJob')
          }
        });
        if (currentJobIdRef.current === jobId) {
          onLoadError?.();
        }
      } finally {
        setLoadingJobId((prev) => (prev === jobId ? null : prev));
      }
    },
    [setLoadingJobId, catchError, dispatch, onLoadError, t]
  );

  const scheduleRefresh = useCallback(
    (jobId: string): Promise<void> => {
      if (refreshTimersRef.current.jobs) {
        window.clearTimeout(refreshTimersRef.current.jobs);
      }
      if (refreshTimersRef.current.details) {
        window.clearTimeout(refreshTimersRef.current.details);
      }

      return new Promise((resolve) => {
        refreshTimersRef.current.jobs = window.setTimeout(() => {
          dispatch(fetchJobsData());
        }, DEBOUNCE_JOBS_TIMEOUT_MS);

        refreshTimersRef.current.details = window.setTimeout(async () => {
          try {
            await loadActiveJobDetails(jobId);
          } finally {
            resolve();
          }
        }, DEBOUNCE_JOBS_TIMEOUT_MS);
      });
    },
    [dispatch, loadActiveJobDetails]
  );

  useEffect(() => {
    return () => {
      if (refreshTimersRef.current.jobs) {
        window.clearTimeout(refreshTimersRef.current.jobs);
      }
      if (refreshTimersRef.current.details) {
        window.clearTimeout(refreshTimersRef.current.details);
      }
    };
  }, []);

  useEffect(() => {
    if (!jobId) {
      return;
    }
    loadActiveJobDetails(jobId);
  }, [jobId, loadActiveJobDetails]);

  const job = jobId && activeJobDetails?.id === jobId ? activeJobDetails : null;
  const isViewLoading = loadingJobId === jobId || (!!jobId && !job);

  const subscriptionStatusShowsUpdatePaymentConfirm =
    job?.subscriptionStatus != null &&
    SUBSCRIPTION_STATUSES_REQUIRING_UPDATE_PAYMENT_CONFIRM.includes(
      job.subscriptionStatus
    );

  useEffect(() => {
    if (
      isUpdatePaymentConfirmModalVisible &&
      !subscriptionStatusShowsUpdatePaymentConfirm
    ) {
      setIsUpdatePaymentConfirmModalVisible(false);
    }
  }, [
    isUpdatePaymentConfirmModalVisible,
    subscriptionStatusShowsUpdatePaymentConfirm
  ]);

  const handleReactivate = useCallback(async () => {
    if (!jobId) {
      return;
    }

    setIsPaymentLoading(true);
    try {
      await ClientsService.subscribePaidJob(jobId);
      notify({
        type: AlertType.Success,
        message: t('sidebar.job.notify.jobActive')
      });
      scheduleRefresh(jobId);
    } catch (error) {
      catchError({
        error,
        default: {
          description: t('error.message.couldNotReactivateJob')
        },
        messages: {
          [StatusCodes.NOT_ACCEPTABLE]: {
            description: t('error.message.cantProcessPayment')
          }
        }
      });
    } finally {
      setIsPaymentLoading(false);
    }
  }, [setIsPaymentLoading, catchError, scheduleRefresh, jobId, t]);

  const handleRetryPayment = useCallback(async () => {
    if (!jobId) {
      return;
    }

    setIsPaymentLoading(true);
    try {
      await ClientsService.retryPaymentForPaidJob(jobId);
      notify({
        type: AlertType.Success,
        message: t('sidebar.job.notify.paymentWentThrough')
      });
      await scheduleRefresh(jobId);
      setIsRetryPaymentSuccessModalVisible(true);
    } catch (error) {
      catchError({
        error,
        default: {
          description: t('error.message.couldNotRetryPayment')
        },
        messages: {
          [StatusCodes.NOT_ACCEPTABLE]: {
            description: t('error.message.cantProcessPayment')
          }
        }
      });
    } finally {
      setIsPaymentLoading(false);
    }
  }, [catchError, scheduleRefresh, setIsPaymentLoading, jobId, t]);

  const handleUpdatePayment = useCallback(
    (isRetryPayment: boolean) => () => {
      setIsRetryPayment(isRetryPayment);
      if (subscriptionStatusShowsUpdatePaymentConfirm) {
        setIsUpdatePaymentConfirmModalVisible(true);
        return;
      }
      setIsPaymentMethodModalVisible(true);
    },
    [subscriptionStatusShowsUpdatePaymentConfirm]
  );

  const handleCancelUpdatePaymentMethodModal = useCallback(() => {
    setIsUpdatePaymentConfirmModalVisible(false);
  }, []);

  const handleSuccessUpdatePaymentMethodModal = useCallback(() => {
    setIsUpdatePaymentConfirmModalVisible(false);
    setIsPaymentMethodModalVisible(true);
  }, []);

  const handleCancelRetryPaymentSuccessModal = useCallback(() => {
    setIsRetryPaymentSuccessModalVisible(false);
    setIsPaymentLoading(false);
  }, []);

  const handleActivateJob = useCallback(async () => {
    if (!jobId || !job) {
      return;
    }

    try {
      setIsPaymentLoading(true);
      await jobsService.updateJob(job, false);
      setIsRetryPaymentSuccessModalVisible(false);
      scheduleRefresh(jobId);
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.updatingJob')
        }
      });
    } finally {
      setIsPaymentLoading(false);
    }
  }, [catchError, job, jobId, scheduleRefresh, t]);

  const handlePaymentModalCancel = useCallback(() => {
    setIsPaymentMethodModalVisible(false);
    setIsPaymentLoading(false);
  }, []);

  const handleAddPaymentMethodSuccess = useCallback(() => {
    setIsPaymentMethodModalVisible(false);
    setIsPaymentLoading(false);
    dispatch(getClientPaymentMethodsInfo());

    notify({
      type: AlertType.Success,
      message: t('payment.methodManagement.success')
    });

    if (jobId && isRetryPayment) {
      handleRetryPayment();
    }
  }, [
    dispatch,
    handleRetryPayment,
    setIsPaymentMethodModalVisible,
    setIsPaymentLoading,
    jobId,
    isRetryPayment,
    t
  ]);

  const handlePaymentMethodError = useCallback(() => {
    setIsPaymentLoading(false);
  }, []);

  const handleCloseJob = useCallback(async () => {
    if (!jobId) {
      return;
    }

    try {
      setIsPaymentLoading(true);
      await ClientsService.unsubscribePaidJob(jobId);

      notify({
        type: AlertType.Success,
        message: t('payment.activationSuccess')
      });

      scheduleRefresh(jobId);
    } catch (error) {
      notify({
        type: AlertType.Error,
        message: t('error.message.cancelSubscription')
      });
    } finally {
      setIsPaymentLoading(false);
    }
  }, [setIsPaymentLoading, scheduleRefresh, jobId, t]);

  const handleCloseFreeJob = useCallback(async () => {
    if (!jobId) {
      return;
    }

    try {
      setIsPaymentLoading(true);
      await jobsService.changeJobStatus(jobId, JobStatus.Inactive);

      notify({
        type: AlertType.Success,
        description: t('stageSelector.jobTransition.inactive')
      });

      scheduleRefresh(jobId);
    } catch (error) {
      notify({
        type: AlertType.Error,
        message: t('error.message.updateJobStatus')
      });
    } finally {
      setIsPaymentLoading(false);
    }
  }, [setIsPaymentLoading, scheduleRefresh, jobId, t]);

  const handleSubmitStart = useCallback(() => {
    setIsPaymentLoading(true);
  }, []);

  return (
    <>
      <JobInfoView
        jobId={jobId}
        job={job}
        isLoading={isViewLoading}
        isPaymentLoading={isPaymentLoading}
        onReactivate={handleReactivate}
        onRetryPayment={handleRetryPayment}
        onUpdatePayment={handleUpdatePayment}
        onCloseJob={handleCloseJob}
        onCloseFreeJob={handleCloseFreeJob}
      />

      <AddPaymentMethodModal
        open={isPaymentMethodModalVisible}
        onCancel={handlePaymentModalCancel}
        onSuccess={handleAddPaymentMethodSuccess}
        onError={handlePaymentMethodError}
        onSubmitStart={handleSubmitStart}
        loading={isPaymentLoading}
      />

      <UpdatePaymentConfirmModal
        open={
          isUpdatePaymentConfirmModalVisible &&
          subscriptionStatusShowsUpdatePaymentConfirm
        }
        onCancel={handleCancelUpdatePaymentMethodModal}
        onSuccess={handleSuccessUpdatePaymentMethodModal}
      />

      <RetryPaymentSuccessModal
        open={isRetryPaymentSuccessModalVisible}
        onCancel={handleCancelRetryPaymentSuccessModal}
        jobId={jobId}
        onActivate={handleActivateJob}
      />
    </>
  );
};

export default JobInfoController;
